import {
  BadgeCheck,
  CalendarCheck,
  ClipboardList,
  LineChart,
  Layers,
  MessageCircle,
  Receipt,
  Briefcase,
  Gauge,
} from "lucide-react";

import { SiteShell } from "@/components/site/SiteShell";
import { BenefitGrid } from "@/components/landing/BenefitGrid";
import { CalculatorBand } from "@/components/landing/CalculatorBand";
import { CheckList } from "@/components/landing/CheckList";
import { FinalCta } from "@/components/landing/FinalCta";
import { LandingHero } from "@/components/landing/LandingHero";
import { StickyMobileCta } from "@/components/landing/StickyMobileCta";
import { WhyTtite } from "@/components/landing/WhyTtite";

const SLUG = "bookkeeping-fees";
const SERVICE = "bookkeeping";
const ACCENT = "#2f7d6d";
const CTA = "Calculate my bookkeeping fees";

/** Who actually does the work. Stated once, here. */
const CREDENTIALS = [
  {
    icon: BadgeCheck,
    title: "Chartered Accountants (SA)",
    text: "Your accounting work is reviewed under the oversight of a CA(SA).",
  },
  {
    icon: Receipt,
    title: "Registered Tax Practitioners",
    text: "Tax returns and SARS matters supported by Registered Tax Practitioners.",
  },
  {
    icon: Briefcase,
    title: "12+ years' experience",
    text: "Across external audit, financial reporting, risk and senior finance roles.",
  },
];

const WHY_IT_MATTERS = [
  {
    icon: ClipboardList,
    title: "Records that stay current",
    text: "Your books are kept up to date through the year, not rebuilt in a panic at year-end.",
  },
  {
    icon: CalendarCheck,
    title: "Deadlines someone else is watching",
    text: "The tax, payroll and compliance submissions that apply to you are tracked and filed.",
  },
  {
    icon: LineChart,
    title: "Numbers you can act on",
    text: "Reliable monthly figures, so pricing, spending and hiring decisions rest on something real.",
  },
];

const WE_CAN_HELP = [
  "Processing of bank transactions",
  "Bank reconciliations",
  "Customer and supplier reconciliations",
  "Management accounts",
  "Annual financial statements",
  "Income tax and provisional tax",
  "VAT returns, where applicable",
  "Payroll and payslips",
  "PAYE, UIF and SDL submissions",
  "CIPC annual returns",
  "Beneficial ownership submissions",
  "COIDA returns",
  "Assistance with SARS queries",
];

/**
 * How we work - no credential is repeated here.
 *
 * The pricing model leads, because it is the real differentiator: the fee
 * follows the work required, not what the business earns.
 */
const WHY_TTITE = [
  {
    icon: Gauge,
    title: "Pricing that doesn't penalise growth",
    text: "Growing your revenue doesn't automatically mean paying more for bookkeeping. Our fee is based on the work required, not a percentage of your revenue.",
  },
  {
    icon: MessageCircle,
    title: "Responsive support",
    text: "Get responsive support from a team you can contact directly when accounting, payroll or tax questions come up.",
  },
  {
    icon: Layers,
    title: "One team. Less hassle.",
    text: "Monthly bookkeeping, year-end financial statements, payroll, tax and statutory filings supported by one team instead of multiple providers.",
  },
];

/** Paid-traffic landing page for monthly bookkeeping. */
export default function BookkeepingFeesPage() {
  return (
    <SiteShell testId="bookkeeping-fees-page">
      <div style={{ "--tt-accent": ACCENT }}>
        <LandingHero
          slug={SLUG}
          service={SERVICE}
          eyebrow="Monthly bookkeeping"
          heading="Bookkeeping that keeps your business"
          highlight="on track"
          lead="Monthly bookkeeping, accounting and tax support for growing businesses — backed by responsive professional support when you need it."
          ctaLabel={CTA}
          note="An indicative monthly fee in about 2 minutes. No obligation."
          credentials={CREDENTIALS}
        />

        <BenefitGrid
          slug={SLUG}
          heading="Spend less time on the accounting, more on the business"
          lead="You didn't start a business to capture invoices. We take the books off your desk and keep them current, so the records are ready whenever SARS, a bank or a buyer asks."
          items={WHY_IT_MATTERS}
        />

        <CheckList
          slug={SLUG}
          heading="What we can look after"
          items={WE_CAN_HELP}
          asideTitle="How it's scoped"
          note="Not everything is bundled into every package. The calculator asks what you need and prices the relevant services. If you need something outside the standard scope, we'll discuss it with you first."
        />

        <CalculatorBand
          slug={SLUG}
          service={SERVICE}
          heading="See what your monthly bookkeeping could cost"
          text="Your fee is based on your transaction volume, payroll and the services you need — not your revenue. Once agreed, your monthly fee remains consistent and may be reviewed periodically if your transaction volumes change significantly."
          ctaLabel={CTA}
          fineprint="Indicative estimate only. The final fee is confirmed once we understand the required scope."
        />

        <WhyTtite slug={SLUG} heading="Why choose T-TITE?" items={WHY_TTITE} />

        <FinalCta
          slug={SLUG}
          service={SERVICE}
          heading="Ready to hand over the bookkeeping?"
          text="Build your own indicative monthly fee, or call and we'll talk it through."
          ctaLabel={CTA}
          secondary="call"
        />

        <StickyMobileCta slug={SLUG} service={SERVICE} label="Calculate fees" />
      </div>
    </SiteShell>
  );
}
